/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useParams } from 'react-router-dom'

import { EuiSkeletonText } from '@elastic/eui'

import { useGetProjectQuery } from '@modules/project-admin-lib/hooks/get'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import ProjectCpsLinkPage from '@modules/project-components/ProjectCps/wizard/ProjectCpsLinkPage'
import { AdminConsoleProvider } from '@modules/project-components/ProjectCps/providers'

type Props = {
  projectType: ProjectType
}

/**
 * Wrapper component for the admin console that:
 * 1. Fetches the project to get the organization_id
 * 2. Provides the AdminConsoleProvider context with the organization_id
 * 3. Renders the shared ProjectCpsLinkPage component
 */
const ProjectCpsLinkPageWrapper: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams() as { projectId: ProjectID }

  const {
    data: project,
    error: projectError,
    isLoading,
  } = useGetProjectQuery(projectType, projectId)

  if (isLoading) {
    return <EuiSkeletonText />
  }

  if (projectError) {
    return <ProjectApiErrorCallout error={projectError} />
  }

  if (!project) {
    return <EuiSkeletonText />
  }

  const organizationId = project.metadata.organization_id

  if (!organizationId) {
    return <ProjectApiErrorCallout error='Organization ID not found for this project' />
  }

  return (
    <AdminConsoleProvider organizationId={organizationId}>
      <ProjectCpsLinkPage projectType={projectType} />
    </AdminConsoleProvider>
  )
}

export default ProjectCpsLinkPageWrapper
